// Data derivation for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView `data.attrs` / `data.points`
// (bpm/mds/chart.coffee). Pure — no DOM, no rendering.

import { noTextPlaceholder, removeLogicFromText } from '@aytm/app/stats/page/components/lib/utils'

// Axes whose angles differ by less than this (radians) are merged into one group.
const ANGLE_MERGE_THRESHOLD = 0.07

// _.min / _.max by iteratee
const minBy = (arr, fn) => arr.reduce((m, d) => (fn(d) < fn(m) ? d : m), arr[0])
const maxBy = (arr, fn) => arr.reduce((m, d) => (fn(d) > fn(m) ? d : m), arr[0])

// removeLogicFromText → noTextPlaceholder fallback → strip <u> tags (legacy order)
const cleanText = (raw, fallbackEl) => {
  const text = removeLogicFromText(raw) || (fallbackEl ? noTextPlaceholder(fallbackEl) : '')
  return text.replace(/<u>/g, '').replace(/<\/u>/g, '')
}

// Only integer centers are honoured, otherwise 0 (mirrors legacy guard)
const resolveCenter = center => ({
  x: (Number.isInteger(center?.x) && center.x) || 0,
  y: (Number.isInteger(center?.y) && center.y) || 0
})

/**
 * Attribute vector-axes for the MDS map.
 * @returns {{ axises: Array, labels: Array }} axises sorted by length desc with `index`.
 */
export const computeAxes = ({ answer, batches, center }) => {
  const axises = []
  const labels = []
  const nearest = {}
  const _center = resolveCenter(center)

  const attrs = answer?.mds?.attrs || {}
  let i = 0
  for (const k of Object.keys(attrs)) {
    const v = attrs[k]
    const angle = Math.atan2(v[1] - _center.y, v[0] - _center.x)
    const length = v[0] ** 2 + v[1] ** 2

    // textPath direction flag: false when x < 0 (both y>0 and y<0)
    const sign = !(Math.sign(v[0]) < 0 && (Math.sign(v[1]) > 0 || Math.sign(v[1]) < 0))

    const axis = {
      id: batches[i]?.position?.toString() || k,
      val: k,
      x: v[0],
      y: v[1] * -1, // revert Y axis
      text: cleanText(batches[i]?.text, batches[i]),
      a: angle,
      l: length,
      s: sign
    }

    // Merge into an existing group if within the angle threshold of any member
    let merged = false
    for (const key of Object.keys(nearest)) {
      if (merged) break
      for (const member of nearest[key]) {
        if (Math.abs(angle - member.a) < ANGLE_MERGE_THRESHOLD) {
          const min = minBy(nearest[key], d => d.a)
          const max = maxBy(nearest[key], d => d.a)
          nearest[key].averX = (max.x + min.x) / 2
          nearest[key].averY = (max.y + min.y) / 2
          nearest[key].averAngle = min.a + (max.a + min.a) / 2
          nearest[key].push(axis)
          merged = true
          break
        }
      }
    }

    if (!merged) nearest[k] = [axis]
    i++
  }

  for (const key of Object.keys(nearest)) {
    const group = nearest[key]

    if (group.length > 1) {
      const ids = []
      const texts = []
      const vals = []
      let lastX = 0
      let lastY = 0
      let lastAngle = 0

      // Project each axis onto the normal of the group's average vector
      for (const axis of group) {
        const normal = -axis.x * group.averX - axis.y * group.averY
        const vl = group.averY ** 2 + group.averX ** 2
        const x = vl !== 0 ? (-group.averX * normal) / vl : 0
        const y = group.averY !== 0 ? (-normal - group.averX * x) / group.averY : 0

        ids.push(axis.id)
        texts.push(axis.text)
        vals.push(axis.val)

        axis.x = x
        axis.y = y
        axis.a = Math.atan2(y - _center.y, x - _center.x)
        axises.push(axis)

        lastX = x
        lastY = y
        lastAngle = axis.a
      }

      labels.push({ id: ids, x: lastX, y: lastY, text: texts, val: vals, a: lastAngle })
    } else {
      group[0].group = false
      axises.push(group[0])
      labels.push({
        id: group[0].id,
        x: group[0].x,
        y: group[0].y,
        a: group[0].a, // original angle, not recomputed (legacy)
        text: group[0].text,
        val: group[0].val
      })
    }
  }

  // Sort by length descending; index drives top-3 emphasis (bold / longer arrows)
  axises.sort((a, b) => a.l - b.l).reverse()
  axises.forEach((a, idx) => {
    a.index = idx
  })

  return { axises, labels }
}

/**
 * Brand points in MDS space.
 * @returns {Array} points with `{ id, val, pos, x, y, label:{ text, w, h } }`.
 */
export const computePoints = ({ answer, questionVariants }) => {
  const points = []
  const brands = answer?.mds?.brands || {}

  let i = 0
  for (const k of Object.keys(brands)) {
    const v = brands[k]
    const xShift = i / 10 ** String(v[0]).length
    const yShift = i / 10 ** String(v[1]).length
    const text = cleanText(questionVariants[i]?.text, questionVariants[i])

    points.push({
      id: questionVariants[i]?.position?.toString() || k,
      val: k,
      pos: questionVariants[i]?.position || i,
      x: v[0] + xShift,
      y: (v[1] + yShift) * -1, // revert Y axis
      label: { text, w: text.length * 8, h: 18 }
    })
    i++
  }

  return points
}
